import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import processEvent from 'analytics/processEvent';
import basketConstants from 'constants/Basket';
import { DELIVERY_METHOD_TYPES } from 'constants/RwdBasket';
import Empty from 'constants/empty';

const {
    ASYNC_PAGE_LOAD, LINK_TRACKING_EVENT, FULFILLMENT_TYPE, PAGE_NAMES, PAGE_DETAIL, ACTION_INFO
} = anaConsts;

export const SFL_ACTIONS = {
    SAVE: 'save',
    VIEW_ALL: 'viewAll',
    CHOOSE_METHOD: 'chooseMethod',
    MOVE_TO_BASKET: 'moveToBasket',
    REMOVE: 'remove',
    UNDO: 'undo'
};

export const SFL_CONTEXTS = {
    CAROUSEL: 'carousel',
    MODAL: 'modal'
};

const SFL_PAGE_NAMES = {
    CAROUSEL: `${PAGE_NAMES.BASKET}:${PAGE_NAMES.BASKET}:n/a:*`,
    MODAL: `${PAGE_NAMES.BASKET}:${PAGE_DETAIL.SAVE_FOR_LATER}:view all:*`,
    CHOOSE_METHOD: `${PAGE_NAMES.BASKET}:${PAGE_DETAIL.SAVE_FOR_LATER}:choose method:*`
};

function triggerAnalytics(eventType, analyticsData) {
    processEvent.process(eventType, { data: { ...analyticsData } });
}

function getFulfillmentTypeFromBasketType(basketType) {
    const { BasketType } = basketConstants;

    switch (basketType) {
        case BasketType.BOPIS:
            return FULFILLMENT_TYPE.Pickup;
        case BasketType.SameDay:
            return FULFILLMENT_TYPE.Sameday;
        case BasketType.AutoReplenish:
            return FULFILLMENT_TYPE.AutoReplenish;
        case BasketType.Standard:
        default:
            return FULFILLMENT_TYPE.Standard;
    }
}

function mapDeliveryOptionToFulfillment(deliveryOption) {
    switch (deliveryOption) {
        case DELIVERY_METHOD_TYPES.BOPIS:
            return FULFILLMENT_TYPE.Pickup;
        case DELIVERY_METHOD_TYPES.SAMEDAY:
            return FULFILLMENT_TYPE.Sameday;
        case DELIVERY_METHOD_TYPES.AUTOREPLENISH:
            return FULFILLMENT_TYPE.AutoReplenish;
        default:
            return FULFILLMENT_TYPE.Standard;
    }
}

function buildProductString(skuId, fulfillmentType = null) {
    let productString = `;${skuId};;;;eVar26=${skuId}`;

    if (fulfillmentType) {
        productString += `|eVar133=${fulfillmentType}`;
    }

    return productString;
}

function resolveFulfillmentType({ basketType, deliveryOption }) {
    if (deliveryOption) {
        return mapDeliveryOptionToFulfillment(deliveryOption);
    }

    if (basketType !== undefined) {
        return getFulfillmentTypeFromBasketType(basketType);
    }

    return null;
}

function getPageName(action, context) {
    if (action === SFL_ACTIONS.CHOOSE_METHOD || action === SFL_ACTIONS.MOVE_TO_BASKET) {
        return SFL_PAGE_NAMES.CHOOSE_METHOD;
    }

    if (action === SFL_ACTIONS.VIEW_ALL || context === SFL_CONTEXTS.MODAL) {
        return SFL_PAGE_NAMES.MODAL;
    }

    return anaUtils.getPageInfo().pageName || SFL_PAGE_NAMES.CAROUSEL;
}

function getActionInfo(action) {
    const map = {
        [SFL_ACTIONS.SAVE]: ACTION_INFO.SAVE_FOR_LATER,
        [SFL_ACTIONS.MOVE_TO_BASKET]: ACTION_INFO.SAVE_FOR_LATER_MOVE_TO_BASKET,
        [SFL_ACTIONS.REMOVE]: ACTION_INFO.SAVE_FOR_LATER_REMOVE,
        [SFL_ACTIONS.UNDO]: ACTION_INFO.SAVE_FOR_LATER_UNDO
    };

    return map[action] || Empty.String;
}

/**
 * Trigger a link tracking event (s.tl) for a Save for Later user action.
 * @param {string} options.action - SFL_ACTIONS constant
 * @param {string} [options.context] - SFL_CONTEXTS constant (CAROUSEL or MODAL)
 * @param {string} options.skuId
 * @param {string} [options.basketType] - used for SAVE action
 * @param {string} [options.deliveryOption] - used for MOVE_TO_BASKET action
 */
export function triggerSaveForLaterLinkEvent({
    action, context, skuId, basketType, deliveryOption
}) {
    const fulfillmentType = resolveFulfillmentType({ basketType, deliveryOption });

    triggerAnalytics(LINK_TRACKING_EVENT, {
        pageName: getPageName(action, context),
        actionInfo: getActionInfo(action),
        productStrings: buildProductString(skuId, fulfillmentType)
    });
}

/**
 * Trigger a page load event (s.t) for Save for Later navigation.
 * @param {string} options.action - SFL_ACTIONS constant (VIEW_ALL or CHOOSE_METHOD)
 * @param {string} [options.context] - SFL_CONTEXTS constant (CAROUSEL or MODAL)
 * @param {string} [options.skuId] - required for CHOOSE_METHOD
 */
export function triggerSaveForLaterPageLoad({ action, context, skuId }) {
    const analyticsData = {
        pageName: getPageName(action, context)
    };

    if (skuId) {
        analyticsData.productStrings = buildProductString(skuId);
    }

    if (action === SFL_ACTIONS.CHOOSE_METHOD) {
        analyticsData.previousPageName = getPageName(null, context);
    }

    triggerAnalytics(ASYNC_PAGE_LOAD, analyticsData);
}
